import { useEffect, useMemo, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  ChevronLeft,
  ChevronRight,
  Loader2,
  Search,
  Phone,
  ArrowUpDown,
  Download,
} from "lucide-react";
import { toast } from "sonner";
import {
  buildUsersCsvUrl,
  formatDate,
  formatDuration,
  formatUsd,
  listUsers,
} from "@/lib/api";

const SORT_FIELDS = [
  { key: "last_contact_unix", label: "Last Seen" },
  { key: "first_contact_unix", label: "First Seen" },
  { key: "sessions", label: "Sessions" },
  { key: "total_duration", label: "Duration" },
  { key: "total_messages", label: "Messages" },
  { key: "total_cost_usd", label: "Cost" },
  { key: "total_credits_used", label: "Credits" },
  { key: "phone", label: "Phone" },
];

function unixToDate(unix) {
  if (!unix || unix <= 0) return "—";
  try {
    return formatDate(new Date(unix * 1000).toISOString());
  } catch {
    return "—";
  }
}

function TagList({ items, testId }) {
  const list = (items || []).filter(Boolean);
  if (list.length === 0) return <span className="text-black/35">—</span>;
  return (
    <div data-testid={testId} className="flex flex-wrap gap-1 max-w-[220px]">
      {list.slice(0, 3).map((v) => (
        <span
          key={v}
          className="inline-block border border-black/15 bg-black/[0.03] px-1.5 py-0.5 font-mono-plex text-[10px] text-black/70"
        >
          {v}
        </span>
      ))}
      {list.length > 3 && (
        <span className="font-mono-plex text-[10px] text-black/45">
          +{list.length - 3}
        </span>
      )}
    </div>
  );
}

function SuccessBar({ rate }) {
  if (rate === null || rate === undefined) {
    return <span className="text-black/35">—</span>;
  }
  const color =
    rate >= 75 ? "#16A34A" : rate >= 40 ? "#CA8A04" : "#DC2626";
  return (
    <div className="flex items-center gap-2 min-w-[100px]">
      <div className="flex-1 h-1.5 bg-black/10">
        <div
          className="h-full transition-all"
          style={{ width: `${Math.min(100, rate)}%`, background: color }}
        />
      </div>
      <span className="font-mono-plex text-[11px] text-black/75 w-9 text-right">
        {rate.toFixed(0)}%
      </span>
    </div>
  );
}

export default function UsersTable({ onOpenUser }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 50;
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [sortBy, setSortBy] = useState("last_contact_unix");
  const [sortDir, setSortDir] = useState("desc");
  const initialLoad = useRef(true);

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    setPage(1);
  }, [debouncedQ, sortBy, sortDir]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listUsers({
      q: debouncedQ || undefined,
      page,
      page_size: pageSize,
      sort_by: sortBy,
      sort_dir: sortDir,
    })
      .then((data) => {
        if (cancelled) return;
        setRows(data.items || []);
        setTotal(data.total || 0);
      })
      .catch((e) => {
        if (cancelled) return;
        if (!initialLoad.current) toast.error("Failed to load users");
      })
      .finally(() => {
        initialLoad.current = false;
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedQ, page, sortBy, sortDir]);

  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  const toggleSort = (key) => {
    if (sortBy === key) {
      setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    } else {
      setSortBy(key);
      setSortDir("desc");
    }
  };

  const HeaderCell = ({ label, sortKey, align = "left", className = "" }) => {
    const active = sortBy === sortKey;
    return (
      <th
        className={`text-${align} py-3 px-4 font-mono-plex text-[10px] font-bold uppercase tracking-[0.2em] text-black/55 whitespace-nowrap ${className}`}
      >
        {sortKey ? (
          <button
            data-testid={`users-sort-${sortKey}`}
            onClick={() => toggleSort(sortKey)}
            className={`inline-flex items-center gap-1 hover:text-black ${
              active ? "text-black" : ""
            }`}
          >
            {label}
            <ArrowUpDown
              className={`w-3 h-3 ${active ? "opacity-100" : "opacity-40"}`}
            />
            {active && (
              <span className="font-mono-plex text-[8px] text-black/50">
                {sortDir === "desc" ? "↓" : "↑"}
              </span>
            )}
          </button>
        ) : (
          label
        )}
      </th>
    );
  };

  return (
    <div data-testid="users-view" className="flex flex-col min-h-full bg-white">
      {/* Toolbar */}
      <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-4 px-5 py-4 border-b border-black/10">
        <div className="flex items-center gap-2">
          <h2 className="font-heading text-sm font-bold uppercase tracking-[0.25em] text-black/80">
            Users
          </h2>
          <span className="font-mono-plex text-[10px] uppercase tracking-[0.2em] text-black/45">
            {total.toLocaleString()} unique
          </span>
        </div>
        <div className="flex-1" />
        <div className="relative w-full md:w-72">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-black/40" />
          <Input
            data-testid="users-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="search phone number…"
            className="h-9 pl-8 rounded-none bg-white border-black/15 text-black placeholder:text-black/35 font-mono-plex text-xs focus-visible:ring-1 focus-visible:ring-black/30"
          />
        </div>
        <a
          data-testid="users-export-csv"
          href={buildUsersCsvUrl({
            q: debouncedQ || undefined,
            sort_by: sortBy,
            sort_dir: sortDir,
          })}
          className="h-9 inline-flex items-center justify-center gap-2 rounded-none border border-black bg-black text-white px-3 text-xs font-medium hover:bg-black/85 transition-colors whitespace-nowrap"
          title="Export current users (filtered) as CSV"
        >
          <Download className="w-3.5 h-3.5" />
          Export CSV
        </a>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        {loading && rows.length === 0 ? (
          <div className="py-24 text-center">
            <Loader2 className="w-5 h-5 animate-spin inline-block text-black/40" />
          </div>
        ) : rows.length === 0 ? (
          <div className="py-24 text-center font-mono-plex text-xs uppercase tracking-[0.2em] text-black/40">
            no users match
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-white border-b border-black/10 z-10">
              <tr>
                <HeaderCell label="Phone" sortKey="phone" />
                <HeaderCell label="Sessions" sortKey="sessions" align="right" />
                <HeaderCell label="Msgs" sortKey="total_messages" align="right" />
                <HeaderCell label="Duration" sortKey="total_duration" align="right" />
                <HeaderCell label="Cost $" sortKey="total_cost_usd" align="right" />
                <HeaderCell label="Credits" sortKey="total_credits_used" align="right" />
                <HeaderCell label="Success" sortKey={null} />
                <HeaderCell label="First Seen" sortKey="first_contact_unix" />
                <HeaderCell label="Last Seen" sortKey="last_contact_unix" />
                <HeaderCell label="Agents" sortKey={null} />
                <HeaderCell label="Lang" sortKey={null} />
                <HeaderCell label="Source" sortKey={null} />
              </tr>
            </thead>
            <tbody>
              {rows.map((u) => (
                <tr
                  key={u.phone}
                  data-testid={`user-row-${u.phone}`}
                  className="border-b border-black/5 hover:bg-black/[0.02] cursor-pointer"
                  onClick={() => onOpenUser?.(u)}
                >
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <Phone className="w-3.5 h-3.5 text-black/40" />
                      <span className="font-mono-plex text-xs text-black tracking-wide">
                        {u.phone}
                      </span>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-right font-mono-plex text-xs text-black">
                    {u.sessions?.toLocaleString?.() ?? "—"}
                  </td>
                  <td className="py-3 px-4 text-right font-mono-plex text-xs text-black/80">
                    {u.total_messages?.toLocaleString?.() ?? "—"}
                  </td>
                  <td className="py-3 px-4 text-right font-mono-plex text-xs text-black/80">
                    {formatDuration(u.total_duration)}
                  </td>
                  <td className="py-3 px-4 text-right font-mono-plex text-xs text-black/80">
                    {formatUsd(u.total_cost_usd)}
                  </td>
                  <td className="py-3 px-4 text-right font-mono-plex text-xs text-black/80">
                    {u.total_credits_used?.toLocaleString?.() ?? "—"}
                  </td>
                  <td className="py-3 px-4">
                    <SuccessBar rate={u.success_rate} />
                  </td>
                  <td className="py-3 px-4 font-mono-plex text-[11px] text-black/70 whitespace-nowrap">
                    {unixToDate(u.first_contact_unix)}
                  </td>
                  <td className="py-3 px-4 font-mono-plex text-[11px] text-black/70 whitespace-nowrap">
                    {unixToDate(u.last_contact_unix)}
                  </td>
                  <td className="py-3 px-4">
                    <TagList items={u.agents} testId={`user-agents-${u.phone}`} />
                  </td>
                  <td className="py-3 px-4">
                    <TagList items={u.languages} testId={`user-langs-${u.phone}`} />
                  </td>
                  <td className="py-3 px-4">
                    <TagList items={u.sources} testId={`user-sources-${u.phone}`} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      <div className="border-t border-black/10 px-5 py-3 flex items-center justify-between">
        <span className="font-mono-plex text-[10px] uppercase tracking-[0.2em] text-black/50">
          page {page} of {pageCount} · {total.toLocaleString()} total
        </span>
        <div className="flex items-center gap-2">
          <Button
            data-testid="users-prev-page"
            variant="outline"
            size="sm"
            disabled={page <= 1 || loading}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="h-8 rounded-none border-black/15 hover:bg-black/[0.04]"
          >
            <ChevronLeft className="w-3.5 h-3.5" />
          </Button>
          <Button
            data-testid="users-next-page"
            variant="outline"
            size="sm"
            disabled={page >= pageCount || loading}
            onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
            className="h-8 rounded-none border-black/15 hover:bg-black/[0.04]"
          >
            <ChevronRight className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}
